import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { offsetForClockTime, resolveTimeline } from '@vendorops/shared';
import { client } from '@/lib/api';
import type { TimelineRetime } from '@/hooks/use-timeline-drag';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import TimelineAxis from '@/components/TimelineAxis';
import type {
  CoveragePlacement,
  CoverageRetime,
  CoverageTags,
  TrackCoverage,
} from '@/components/CoverageTrack';
import {
  TimelineItemForm,
  type TimelineItemDraft,
  type TimelineItemPlacement,
  type TimelineItemView,
} from '@/components/TimelineItem';

/**
 * A wedding's day-of timeline (PRD §F2).
 *
 * The planner enters the hours; the day is drafted from their own template and
 * edited here. Every clock time, day offset and resolved duration on screen was
 * computed by the server — one item's length depends on its neighbour's start,
 * so every mutation returns the whole timeline and this panel simply renders
 * what came back.
 *
 * The three ways to have no rows on screen are three different situations and
 * get three different answers. `storedItemCount`, not `items.length`, is what
 * tells them apart: a wedding whose hours were blanked mid-edit has its items
 * still on disk and nothing to anchor them to, and offering to "generate a
 * timeline" there would tell the planner that work they can still get back is
 * gone.
 */

async function throwOnError(
  res: { ok: boolean; json: () => Promise<unknown> },
  fallback: string,
): Promise<void> {
  if (res.ok) return;
  const body = (await res.json().catch(() => null)) as { error?: string } | null;
  throw new Error(body?.error ?? fallback);
}

export default function TimelinePanel({
  weddingId,
  onEditHours,
}: {
  weddingId: string;
  /** Opens the wedding's edit dialog — the only place the hours are set. */
  onEditHours: () => void;
}) {
  const queryClient = useQueryClient();
  const timelineKey = ['wedding-timeline', weddingId];
  const coverageKey = ['wedding-coverage', weddingId];
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: timelineKey,
    queryFn: async () => {
      const res = await client.api.weddings[':id'].timeline.$get({ param: { id: weddingId } });
      if (!res.ok) throw new Error('Failed to load the timeline');
      return res.json();
    },
    enabled: !!weddingId,
  });

  /**
   * The vendors' booked hours — a second query rather than part of the
   * timeline's payload, because the two endpoints behave differently on a
   * first read: `GET /timeline` materializes the default twelve-item ladder,
   * and coverage materializes nothing. See `backend/src/routes/coverage.ts`
   * for why folding them together would make opening a screen the thing that
   * drafts a wedding's day.
   */
  const { data: coverage } = useQuery({
    queryKey: coverageKey,
    queryFn: async () => {
      const res = await client.api.weddings[':id'].coverage.$get({ param: { id: weddingId } });
      if (!res.ok) throw new Error('Failed to load the coverage windows');
      return res.json();
    },
    enabled: !!weddingId,
  });

  /**
   * The planner's own categories, which a coverage window's tag is picked from.
   *
   * The same `['categories']` key `AddVendorDialog` reads its picker from, so
   * the two cannot be looking at different lists. Read here and handed to the
   * axis rather than fetched inside the tag control, because the axis is given
   * every fact it draws — see `CoverageTrack`.
   */
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await client.api.categories.$get();
      if (!res.ok) throw new Error('Failed to load categories');
      return (await res.json()) as { id: string; name: string }[];
    },
  });

  /**
   * Every mutation answers with the whole list it touched, and every one of
   * them invalidates rather than writing that answer into the cache: the
   * wedding's own query carries the hours these times are derived from, and one
   * cache write here would be the second place that pairing could go stale.
   *
   * Parameterized by key because both payloads behave this way for the same
   * reason — a window's `hours` string and an item's `clockTime` are both the
   * server's arithmetic over the wedding's start time — and a second copy of
   * these three handlers is a second place for the error line to be forgotten.
   */
  const settled = (key: readonly unknown[]) => ({
    onMutate: () => setError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (err: Error) => setError(err.message),
  });

  const addItem = useMutation({
    mutationFn: async (draft: TimelineItemDraft | TimelineItemPlacement) => {
      const res = await client.api.weddings[':id'].timeline.items.$post({
        param: { id: weddingId },
        // Whichever way the planner said when. The form's clock time is a
        // guess the server resolves; a click's offset is not a guess, and
        // sending it as a clock time would put the morning in the small hours.
        json: {
          ...('offsetMinutes' in draft
            ? { offsetMinutes: draft.offsetMinutes }
            : { clockTime: draft.clockTime }),
          label: draft.label,
          durationMinutes: draft.durationMinutes,
          note: draft.note,
        },
      });
      await throwOnError(res, 'Could not add that item');
    },
    ...settled(timelineKey),
  });

  /**
   * Removing an item, with a way back.
   *
   * Undo rather than a confirmation: a confirm taxes every delete to protect the
   * rare one, and a planner tidying up a draft day does this a lot. The whole
   * item goes in as the variable, not just its id, because the row is off screen
   * by the time the offer is made and what it carried has to come from
   * somewhere.
   */
  const removeItem = useMutation({
    mutationFn: async (item: TimelineItemView) => {
      const res = await client.api.weddings[':id'].timeline.items[':itemId'].$delete({
        param: { id: weddingId, itemId: item.id },
      });
      await throwOnError(res, 'Could not remove that item');
    },
    onMutate: () => setError(null),
    onSuccess: (_result, item) => {
      queryClient.invalidateQueries({ queryKey: timelineKey });
      toast.success(`Removed ${item.label}`, {
        action: {
          label: 'Undo',
          // Restored by value, never by id: the row is deleted for good and a
          // fresh id is no loss. What must survive is what the planner typed —
          // the note especially, which is the one part they cannot retype from
          // memory — and the offset, so it lands back exactly where it was.
          //
          // The offset and not the clock time it was drawn as, which is the
          // click's argument one step further on. A planner typing "4:15" has
          // not said which day they mean and `offsetForClockTime`'s
          // twelve-hour window is the documented rule for guessing; an undo is
          // not a planner naming a time at all, it is the app putting back the
          // row it just took away. There is nothing to guess, so guessing is
          // pure loss.
          //
          // And it loses in two reachable places, because there are only 1440
          // clock times and the schema stores two days either side of the
          // start: all but one of them name four offsets in that range, and a
          // clock time cannot say which. The round trip is faithful only
          // inside the single day the window picks — -719 to +720 against a
          // 17:30 start — and everything outside comes back a whole number of
          // calendar days out, one or two depending how far.
          //
          // Below that window: the morning of the wedding's own date, offsets
          // -1050 to -720, which is clock 00:00 to 05:30 and the top of every
          // single-day axis; undoing a midnight load-in filed it at +390, the
          // other midnight. Above it: anything more than twelve hours after
          // the start, which is the same twelve-hour asymmetry read from the
          // other end. +721 is 05:31 the morning after the wedding and came
          // back as -719, the same clock on the wedding's own morning; the
          // hazard runs from there to the edge of the bound, so a +1500 item
          // read 18:30 and came back as +60.
          onClick: () =>
            addItem.mutate({
              offsetMinutes: item.offsetMinutes,
              label: item.label,
              durationMinutes: item.durationMinutes,
              note: item.note,
            }),
        },
      });
    },
    onError: (err: Error) => setError(err.message),
  });

  /** The payload as it actually came back, so the optimistic write is the same shape. */
  type TimelineData = NonNullable<typeof data>;

  /**
   * Every change to an item that already exists: a block dropped somewhere new,
   * a block nudged with an arrow key, or one field committed where it sits.
   *
   * One mutation for all of them because they are one request — a PATCH on one
   * item — and because they need the same thing of the cache. The only
   * mutations on this panel that write it rather than waiting for the round
   * trip, and the only ones that have to: a drag and an in-place field are both
   * gestures the planner is still watching when they end, so a block that sat
   * where they let go of it and then jumped back for half a second would read
   * as the change having failed.
   *
   * The write goes through the *same* `resolveTimeline` the server runs, over
   * the hours that came back in this very payload — so the clock times, the day
   * offsets and the neighbour-dependent lengths are derived rather than
   * guessed, and the invalidation that follows replaces the lot with the
   * server's own answer.
   */
  const changeItem = useMutation({
    mutationFn: async ({ item, patch }: { item: TimelineItemView; patch: TimelineItemPatch }) => {
      const res = await client.api.weddings[':id'].timeline.items[':itemId'].$patch({
        param: { id: weddingId, itemId: item.id },
        json: patch,
      });
      await throwOnError(res, 'Could not save that change');
    },
    onMutate: async ({ item, patch }) => {
      setError(null);
      // An in-flight read would otherwise land after this write and undo it.
      await queryClient.cancelQueries({ queryKey: timelineKey });
      const previous = queryClient.getQueryData<TimelineData>(timelineKey);
      if (previous) queryClient.setQueryData(timelineKey, applied(previous, item.id, patch));
      return { previous };
    },
    onError: (err: Error, _variables, context) => {
      // Put the day back exactly as it was and say so out loud. A silent
      // rollback looks like the change simply did not take.
      if (context?.previous) queryClient.setQueryData(timelineKey, context.previous);
      toast.error(err.message);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: timelineKey });
    },
  });

  const regenerate = useMutation({
    mutationFn: async () => {
      const res = await client.api.weddings[':id'].timeline.regenerate.$post({
        param: { id: weddingId },
      });
      await throwOnError(res, 'Could not draft the timeline');
    },
    ...settled(timelineKey),
  });

  /**
   * Marking a vendor's booked hours.
   *
   * One grammar, where `addItem` takes two — and the difference is that a
   * window is no longer typed anywhere. Both the ways a window gets created now
   * name an offset and a length outright: a sweep on the track, which has
   * pointed at two pixels and a pixel is already an offset, and an undo, which
   * is the app putting back the two numbers the row held. Neither is a planner
   * naming a clock time, so neither goes through `offsetForClockTime`'s
   * twelve-hour guess at which day they meant. (The route still accepts the
   * clock pair; nothing on this screen sends it.)
   *
   * Both tags are always named, one of them null — see `CoveragePlacement` and
   * `coverageTagsNamedTogether`.
   */
  const addCoverage = useMutation({
    mutationFn: async (placement: CoveragePlacement) => {
      const res = await client.api.weddings[':id'].coverage.$post({
        param: { id: weddingId },
        json: {
          category: placement.category,
          label: placement.label,
          offsetMinutes: placement.offsetMinutes,
          durationMinutes: placement.durationMinutes,
        },
      });
      // The fallback is only a fallback. A second window on a category the
      // wedding already covers comes back 409 with a sentence naming that
      // category, and `throwOnError` prefers it — "Could not add that coverage
      // window" would leave the planner with nothing to do next, when the thing
      // to do is edit the window they already have.
      await throwOnError(res, 'Could not add that coverage window');
    },
    ...settled(coverageKey),
  });

  /**
   * Moving a window, re-lengthing it, or swapping which tag it carries.
   *
   * One mutation for all three because they are one request — a PATCH on one
   * row — but **never one payload**: a retime carries the span and no tag, and
   * a retag carries both tags and no span. Each of those is what the route
   * requires of it, and the two rules pull in opposite directions.
   *
   * A retag names *both* tags even though only one changed, because
   * `coverageTagsNamedTogether` refuses a request that names one alone:
   * whether `{ category: null }` leaves a row with no tag at all depends on
   * what is already stored, so the payload has to say which tag the window ends
   * up with and which is cleared.
   *
   * A retime names *neither*, and that is the half the old form could not do.
   * Sending one tag alone is the same 400; sending both is a retag the planner
   * never asked for, filed every time they dragged a capsule. The track's drag
   * commits `{ offsetMinutes, durationMinutes }` and nothing else, which is
   * exactly what the route takes in its offset grammar.
   */
  const changeCoverage = useMutation({
    mutationFn: async ({
      annotation,
      patch,
    }: {
      annotation: TrackCoverage;
      patch: CoverageTags | CoverageRetime;
    }) => {
      const res = await client.api.weddings[':id'].coverage[':annotationId'].$patch({
        param: { id: weddingId, annotationId: annotation.id },
        json: patch,
      });
      // 409 again, with the category named — a retag onto a category that is
      // already covered is the same collision as a second create.
      await throwOnError(res, 'Could not save that coverage window');
    },
    ...settled(coverageKey),
  });

  /**
   * Removing a window, with a way back — the same offer `removeItem` makes,
   * for the same reason: a confirm taxes every delete to protect the rare one.
   * The whole window goes in as the variable rather than its id, because the
   * capsule is off screen by the time the offer is made and what it carried has
   * to come from somewhere.
   */
  const removeCoverage = useMutation({
    mutationFn: async (annotation: TrackCoverage) => {
      const res = await client.api.weddings[':id'].coverage[':annotationId'].$delete({
        param: { id: weddingId, annotationId: annotation.id },
      });
      await throwOnError(res, 'Could not remove that coverage window');
    },
    onMutate: () => setError(null),
    onSuccess: (_result, annotation) => {
      queryClient.invalidateQueries({ queryKey: coverageKey });
      // The tag is the window's name, and exactly one of the pair is set.
      toast.success(`Removed ${annotation.category ?? annotation.label}`, {
        action: {
          label: 'Undo',
          // By `offsetMinutes` and `durationMinutes`, never by the clock times
          // the capsule was drawn with. Every word of the long note on
          // `removeItem`'s undo applies here: a planner typing "4:15" has not
          // said which day they mean and `offsetForClockTime`'s twelve-hour
          // window is the documented rule for guessing, but an undo is not a
          // planner naming a time at all — it is the app putting back the row
          // it just took away, and there is nothing to guess. A clock time
          // cannot say which of the four offsets in the stored range it means,
          // so a midnight window would come back a calendar day out.
          //
          // The POST route takes the offset grammar outright (`requestedSpan`
          // prefers it), so unlike the clock pair there is nothing to convert
          // on the way back in — the two numbers the row held are the two
          // numbers the request carries.
          onClick: () =>
            addCoverage.mutate({
              category: annotation.category,
              label: annotation.label,
              offsetMinutes: annotation.offsetMinutes,
              durationMinutes: annotation.durationMinutes,
            }),
        },
      });
    },
    onError: (err: Error) => setError(err.message),
  });

  const items = data?.items ?? [];
  // Null resolves to no length at all, which is what "runs until the night
  // ends" looks like on the wire. Zero is not that; see TimelineItem.
  const annotations = coverage?.annotations ?? [];
  const categoryNames = (categories ?? []).map((category) => category.name);

  /**
   * Whether there is a day to draw — and coverage counts, not just items.
   *
   * It would be enough to ask about items if coverage were only decoration on
   * them, and it is not: a window is the only thing on this panel that leaves
   * the app. `{{coverage_hours}}` resolves off these rows every time the
   * planner composes, so a window the axis declines to draw is a window still
   * merging into a photographer's email with no way on screen to see it or
   * change it — the capsule and the label on it are the whole of the edit
   * path.
   *
   * And it is reachable rather than theoretical: deleting every item is a
   * supported thing to do, which is what the "Nothing on the schedule yet"
   * state below exists for. A planner who drew their coverage first and then
   * cleared the drafted ladder would have had working windows go invisible
   * while they kept addressing vendors.
   */
  const hasDay = items.length > 0 || annotations.length > 0;

  return (
    <Card className="rounded-3xl bg-card p-5 shadow-ambient">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="label-caps text-xs text-muted-foreground">Day-of timeline</h2>
        <div className="flex items-center gap-2">
          {/* An item is placed by its clock time, so it needs a start time to be
              an offset from. With no hours entered there is nothing to add to. */}
          {data?.startTime && !adding && (
            <Button variant="outline" size="sm" onClick={() => setAdding(true)}>
              <Plus className="size-3.5" />
              Add an item
            </Button>
          )}
          {/* No "Add coverage" beside it, and its absence is the point. A window
              is drawn in the coverage track beside the day — pointer down on
              its empty ground, drag, name the vendor — which is the same
              gesture that makes an event on the day.
              A header button opening a form was a second way to say *when* on a
              timeline where everything else is said by pointing at it, and the
              redundancy was the half of the old design the planner objected to
              first. */}
          {/* "Draft from my template" otherwise lives only in the empty state
              below, and `hasDay` now reaches the axis before that state on a
              wedding with windows and no items — which would have taken the
              offer away exactly when the ladder is the thing that is missing.
              Offered here instead for that one case, so drawing coverage first
              never costs the planner the draft. */}
          {data?.startTime && items.length === 0 && annotations.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              disabled={regenerate.isPending}
              onClick={() => regenerate.mutate()}
            >
              Draft from my template
            </Button>
          )}
        </div>
      </div>

      {error && (
        <p role="alert" className="mb-3 text-xs text-destructive">
          {error}
        </p>
      )}

      {isLoading && <p className="text-sm text-muted-foreground">Loading the day…</p>}
      {isError && (
        <p className="text-sm text-muted-foreground">
          The timeline didn’t load. Reload the page to try again.
        </p>
      )}

      {data &&
        (hasDay ? (
          /* `anchorKey` is the day the axis places its view against: the
             timeline opens scrolled to event start once per wedding, not again
             on every refetch this panel does after an edit. */
          <TimelineAxis
            items={items}
            startTime={data.startTime}
            endTime={data.endTime}
            weddingDate={data.weddingDate}
            anchorKey={weddingId}
            /* Resolved on the server and drawn as given: the `hours` string on
               each window is the one `{{coverage_hours}}` merges into a
               vendor's email, so the axis formats none of it. */
            coverage={annotations}
            categories={categoryNames}
            /* Four intents, four mutations, no form. The axis owns the
               gestures and the drafts; this panel owns the requests. */
            onPlaceCoverage={(placement) => addCoverage.mutate(placement)}
            onRetagCoverage={(annotation, tags) =>
              changeCoverage.mutate({ annotation, patch: tags })
            }
            onRetimeCoverage={(annotation, patch) =>
              changeCoverage.mutate({ annotation, patch })
            }
            onRemoveCoverage={(annotation) => removeCoverage.mutate(annotation)}
            deletingId={removeItem.isPending ? (removeItem.variables?.id ?? null) : null}
            onEditField={(item, patch) => changeItem.mutate({ item, patch })}
            onCreate={(draft) => addItem.mutate(draft)}
            onDelete={(item) => removeItem.mutate(item)}
            onRetime={(item, patch) => changeItem.mutate({ item, patch })}
          />
        ) : data.storedItemCount > 0 ? (
          /* Rows on disk with nothing to place them against — a planner who
             blanked the hours field to retype it. Not an empty timeline, and
             emphatically not an invitation to generate a new one. */
          <Empty
            action={
              <Button variant="outline" size="sm" onClick={onEditHours}>
                Add the wedding’s hours
              </Button>
            }
          >
            {data.storedItemCount === 1
              ? '1 item is saved for this day'
              : `${data.storedItemCount} items are saved for this day`}
            , but there’s no start time to place them against. Add the hours and they come back
            exactly as you left them.
          </Empty>
        ) : !data.startTime ? (
          <Empty
            action={
              <Button variant="outline" size="sm" onClick={onEditHours}>
                Add the wedding’s hours
              </Button>
            }
          >
            There’s no start time for this wedding yet, and the whole day is placed against it. Add
            the hours and the timeline drafts itself.
          </Empty>
        ) : (
          <Empty
            action={
              <Button size="sm" disabled={regenerate.isPending} onClick={() => regenerate.mutate()}>
                Draft from my template
              </Button>
            }
          >
            Nothing on the schedule yet. Draft the day from your template, then change whatever this
            wedding needs.
          </Empty>
        ))}

      {adding && data?.startTime && (
        <div className={hasDay ? 'border-t border-border pt-3' : 'mt-3'}>
          <TimelineItemForm
            idPrefix="timeline-new"
            initial={{ clockTime: '', label: '', durationMinutes: null, note: null }}
            submitLabel="Add"
            pending={addItem.isPending}
            onCancel={() => setAdding(false)}
            onSubmit={async (draft) => {
              try {
                await addItem.mutateAsync(draft);
                setAdding(false);
              } catch {
                // The message is already on screen; the form stays open over it
                // so the entry is still there to correct.
              }
            }}
          />
        </div>
      )}

    </Card>
  );
}

/** What one PATCH on an existing item can carry, in both grammars. */
type TimelineItemPatch = TimelineRetime & Partial<TimelineItemDraft>;

/**
 * The day as it will be once the server agrees, derived rather than guessed.
 *
 * A timeline item's clock time, its calendar day and — for an open-ended one —
 * its whole length depend on the wedding's hours and on its neighbours, so
 * writing a new `offsetMinutes` into the cache and leaving the rest is how a
 * block ends up drawn at one time and labelled with another. `resolveTimeline`
 * is the server's own function over the hours the same payload carries; there
 * is no second reading of the rules here to go stale.
 *
 * `clockTime` is the planner's grammar and `offsetMinutes` is the stored one,
 * and the conversion between them is `offsetForClockTime` — the same function
 * the route uses, so a 2:00 PM entry against a 5:30 PM wedding reads as -210
 * here exactly as it will there. A second reading of that rule is the one thing
 * this file must not contain.
 */
function applied<Data extends TimelinePayload>(
  data: Data,
  itemId: string,
  patch: TimelineItemPatch,
): Data {
  const { clockTime, ...stored } = patch;
  const moved = clockTime === undefined ? null : offsetForClockTime(data.startTime, clockTime);
  const change = moved === null ? stored : { ...stored, offsetMinutes: moved };
  const rows = data.items.map((item) => (item.id === itemId ? { ...item, ...change } : item));
  return {
    ...data,
    items: resolveTimeline(rows, data.startTime, data.endTime).map((resolved) => ({
      ...resolved.item,
      clockTime: resolved.clockTime,
      dayOffset: resolved.dayOffset,
      resolvedDurationMinutes: resolved.resolvedDurationMinutes,
      endsAfterWedding: resolved.endsAfterWedding,
      endClockTime: resolved.endClockTime,
      endDayOffset: resolved.endDayOffset,
    })),
  } as Data;
}

interface TimelinePayload {
  startTime: string | null;
  endTime: string | null;
  items: readonly TimelineItemView[];
}

/** A state with no rows in it: one sentence, and the one thing to do about it. */
function Empty({ children, action }: { children: ReactNode; action: ReactNode }) {
  return (
    <div className="rounded-2xl bg-[var(--color-surface-container-high)] px-4 py-4">
      {/* Not `text-muted-foreground`, which is the token every other secondary
          line in this panel uses. It reaches 4.55:1 on the white card those
          lines sit on and only 3.59:1 here, on the filled container — and this
          is the one paragraph a planner has to read to know what to do next.
          `foreground/75` is the lightest step that clears the floor on this
          surface at 5.07:1; /70 comes to 4.43 and misses. */}
      <p className="text-sm text-foreground/75">{children}</p>
      <div className="mt-3">{action}</div>
    </div>
  );
}
